import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Alert,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useAuth } from "../../hooks";
import ErrorDisplay from "../../components/common/ErrorDisplay";
import { trpc } from "../../lib/api/trpc";
import AuthLayout from "../../layouts/AuthLayout";

/**
 * Forced password reset page for users with forcePasswordChange flag
 *
 * Features:
 * - Forces users to change password when forcePasswordChange is true
 * - Cannot navigate away without changing password
 * - Validates password requirements
 * - Confirms password match
 * - Auto-redirects after successful change
 */

const ForcedPasswordResetPage = () => {
  const navigate = useNavigate();
  const {
    user,
    isLoading: authLoading,
    error: authError,
    clearError,
    logout,
    refreshUser,
  } = useAuth();

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const [successMessage, setSuccessMessage] = useState("");
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Password change mutation
  // @ts-ignore - Type checking for trpc
  const changePasswordMutation = trpc.auth.changePassword.useMutation();

  // Redirect if user doesn't need to reset password
  useEffect(() => {
    if (user && !user.forcePasswordChange) {
      navigate("/", { replace: true });
    }
  }, [user, navigate]);

  // Clear error when form values change
  useEffect(() => {
    if (authError && (newPassword || confirmPassword)) {
      clearError();
    }
  }, [newPassword, confirmPassword, authError, clearError]);

  const validatePassword = (password: string): string => {
    if (!password) {
      return "Password is required";
    }
    if (password.length < 8) {
      return "Password must be at least 8 characters";
    }
    if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
      return "Password must contain at least one uppercase letter, one lowercase letter, and one number";
    }
    return "";
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    const newPasswordError = validatePassword(newPassword);
    if (newPasswordError) {
      errors.newPassword = newPasswordError;
    }

    if (!confirmPassword) {
      errors.confirmPassword = "Please confirm your password";
    } else if (newPassword !== confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setIsChangingPassword(true);

      // For forced password reset, we'll send an empty string as current password
      // The API should recognize the forcePasswordChange flag and accept it
      await changePasswordMutation.mutateAsync({
        currentPassword: "", // Empty for forced reset
        newPassword,
        confirmPassword,
      });

      setSuccessMessage("Password changed successfully! Redirecting...");

      // Clear form
      setNewPassword("");
      setConfirmPassword("");

      // Refresh user data to get updated forcePasswordChange flag
      await refreshUser();

      // Redirect immediately now that user data is refreshed
      navigate("/", { replace: true });
    } catch (error: any) {
      // Handle specific errors
      if (error?.message?.includes("Current password is incorrect")) {
        // This shouldn't happen for forced reset, but handle it
        setValidationErrors({
          form: "Unable to reset password. Please contact support.",
        });
      } else {
        setValidationErrors({
          form: error?.message || "Failed to change password",
        });
      }
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/auth/login", { replace: true });
  };

  return (
    <AuthLayout
      title="Password Reset Required"
      subtitle="Your administrator requires you to reset your password before continuing."
      maxWidth={440}
      alert={
        <>
          <Alert severity="warning" sx={{ mb: 3 }}>
            You must change your password to continue using the application.
          </Alert>

          {successMessage && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {successMessage}
            </Alert>
          )}

          {authError && <ErrorDisplay error={authError} onClose={clearError} />}

          {validationErrors.form && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {validationErrors.form}
            </Alert>
          )}
        </>
      }
    >
      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
        <TextField
          margin="normal"
          required
          fullWidth
          name="newPassword"
          label="New Password"
          type={showNewPassword ? "text" : "password"}
          id="newPassword"
          autoComplete="new-password"
          autoFocus
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          error={!!validationErrors.newPassword}
          helperText={
            validationErrors.newPassword ||
            "Must be at least 8 characters with uppercase, lowercase, and number"
          }
          disabled={authLoading || isChangingPassword}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle new password visibility"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  edge="end"
                  disabled={authLoading || isChangingPassword}
                >
                  {showNewPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          margin="normal"
          required
          fullWidth
          name="confirmPassword"
          label="Confirm New Password"
          type={showConfirmPassword ? "text" : "password"}
          id="confirmPassword"
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          error={!!validationErrors.confirmPassword}
          helperText={validationErrors.confirmPassword}
          disabled={authLoading || isChangingPassword}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle confirm password visibility"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  edge="end"
                  disabled={authLoading || isChangingPassword}
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2 }}
          disabled={
            authLoading ||
            isChangingPassword ||
            !newPassword ||
            !confirmPassword
          }
        >
          {isChangingPassword ? (
            <CircularProgress size={24} />
          ) : (
            "Change Password"
          )}
        </Button>

        <Button
          fullWidth
          variant="outlined"
          onClick={handleLogout}
          disabled={authLoading || isChangingPassword}
        >
          Logout
        </Button>
      </Box>
    </AuthLayout>
  );
};

export default ForcedPasswordResetPage;
